import type { GetIntakeFormsAndQuestionsByFormIdsQueryForm } from "@frontend/lib/graphql/gql-types";

export function translateIntakeForm(
  intakeFormToQuestions: GetIntakeFormsAndQuestionsByFormIdsQueryForm["intakeFormToQuestions"],
  locale,
): GetIntakeFormsAndQuestionsByFormIdsQueryForm["intakeFormToQuestions"] {
  // Deep clone the entire array of questions
  const translatedData = intakeFormToQuestions.map((question) => {
    // Create a deep clone of the question object
    const clonedQuestion = JSON.parse(JSON.stringify(question));

    // Store original values
    clonedQuestion.intakeQuestion.originalText =
      question.intakeQuestion.questionText;
    clonedQuestion.intakeQuestion.originalAttributes = JSON.parse(
      JSON.stringify(question.intakeQuestion.questionAttributes),
    );

    // Normalize choice values — some choices in data migrations were entered with
    // trailing spaces (e.g. "Sibling ", "Child "). The config manager UI has no
    // trimming, so this guards against both existing data and future manual entry.
    if (clonedQuestion.intakeQuestion.questionAttributes?.choices) {
      clonedQuestion.intakeQuestion.questionAttributes.choices =
        clonedQuestion.intakeQuestion.questionAttributes.choices.map(
          (choice: { value?: string }) => ({
            ...choice,
            value:
              typeof choice.value === "string"
                ? choice.value.trim()
                : choice.value,
          }),
        );
    }

    // If locale is 'en' or not provided, return cloned question without translation
    if (!locale || locale === "en") {
      return clonedQuestion;
    }

    const translations = question.intakeQuestion.intakeQuestionTranslations;
    const translation = translations?.find((t) => t.languageCode === locale);

    if (translation) {
      // Override questionText if translation exists
      clonedQuestion.intakeQuestion.questionText = translation.questionText;

      // Override questionAttributes if they exist in translation
      if (translation.questionAttributes) {
        const original = clonedQuestion.intakeQuestion.questionAttributes;
        const translated = translation.questionAttributes;

        // If there are choices, only override their values
        if (original?.choices && translated?.choices) {
          clonedQuestion.intakeQuestion.questionAttributes = {
            ...original,
            choices: original.choices.map((choice, index) => ({
              ...choice,
              value: translated.choices[index]?.value || choice.value,
            })),
          };
        }

        if (original.blurb && translated.blurb) {
          clonedQuestion.intakeQuestion.questionAttributes = {
            ...original,
            blurb: translated.blurb,
          };
        }
      }
    }

    return clonedQuestion;
  });

  return translatedData;
}
